<section
  class="runner-job-card"
  [class.runner-job-card--live]="running()"
  data-testid="runner-job-card"
  [attr.aria-labelledby]="titleId()"
>
  <div class="runner-job-card__head">
    <span class="runner-job-card__icon" aria-hidden="true">
      <mat-icon>computer</mat-icon>
    </span>
    <div class="runner-job-card__body">
      <p class="runner-job-card__title" [id]="titleId()">{{ 'testRunner.runnerJob.title' | translate }}</p>
      <div class="runner-job-card__line">
        <span class="runner-job-card__name" data-testid="runner-job-name">
          {{ runnerJob().runnerName ?? ('testRunner.runnerJob.unknownRunner' | translate) }}
        </span>
        <app-res-pill
          data-testid="runner-job-status"
          [status]="pillStatus()"
          [label]="
            (cancelRequested() ? 'testCases.run.cancel.requested' : 'testCases.run.jobStatus.' + status()) | translate
          "
          [pulsing]="running()"
        />
      </div>
      @if (subline().length) {
        <p class="runner-job-card__subline" data-testid="runner-job-subline">
          @for (part of subline(); track part.kind) {
            @if (!$first) {
              <span aria-hidden="true">{{ ' · ' }}</span>
            }
            @switch (part.kind) {
              @case ('engine') {
                @let engineKey = 'runners.engine.' + part.value;
                @let engineText = engineKey | translate;
                <span data-testid="runner-job-engine">{{ engineText === engineKey ? part.value : engineText }}</span>
              }
              @case ('os') {
                <span data-testid="runner-job-os">{{ part.value }}</span>
              }
              @case ('duration') {
                <span data-testid="runner-job-duration">
                  {{ 'testRunner.runnerJob.duration' | translate: { duration: part.value } }}
                </span>
              }
            }
          }
        </p>
      }
    </div>
    <div class="runner-job-card__actions">
      @if (canCancel()) {
        <button
          mat-stroked-button
          type="button"
          class="runner-job-card__cancel"
          data-testid="runner-job-cancel"
          [disabled]="cancelling() || cancelRequested()"
          (click)="askCancel()"
        >
          @if (cancelling()) {
            <mat-progress-spinner diameter="16" mode="indeterminate" aria-hidden="true" />
          } @else {
            <mat-icon aria-hidden="true">stop_circle</mat-icon>
          }
          {{ 'testCases.run.cancel.button' | translate }}
        </button>
      }
      <button
        mat-stroked-button
        type="button"
        data-testid="runner-job-protocol-toggle"
        [attr.aria-expanded]="expanded()"
        [attr.aria-controls]="protocolId()"
        (click)="toggleProtocol()"
      >
        <mat-icon aria-hidden="true">{{ expanded() ? 'expand_less' : 'expand_more' }}</mat-icon>
        {{ (expanded() ? 'testRunner.runnerJob.protocolHide' : 'testRunner.runnerJob.protocolShow') | translate }}
      </button>
    </div>
  </div>

  @if (cancelError(); as key) {
    <p class="runner-job-card__cancel-error" role="alert" data-testid="runner-job-cancel-error">
      <mat-icon aria-hidden="true">error_outline</mat-icon>
      <span>{{ 'testCases.run.cancel.' + key | translate }}</span>
    </p>
  }

  @if (status() === 'ERROR') {
    <div class="runner-job-card__error" data-testid="runner-job-error">
      <mat-icon aria-hidden="true">error_outline</mat-icon>
      <div>
        <p>
          @if (runnerJob().error; as error) {
            {{ 'testRunner.runnerJob.error' | translate: { error } }}
          } @else {
            {{ 'testRunner.runnerJob.errorNoText' | translate }}
          }
        </p>
        @if (!hasStepResults()) {
          <p>{{ 'testRunner.runnerJob.noSteps' | translate }}</p>
        }
      </div>
    </div>
  }

  @if (unreachableSince(); as lastSeen) {
    <p class="runner-job-card__unreachable" data-testid="runner-job-unreachable">
      {{ 'testRunner.runnerJob.unreachable' | translate: { time: (lastSeen | date: 'HH:mm') } }}
    </p>
  }

  <div
    class="runner-job-card__protocol"
    role="region"
    data-testid="runner-job-protocol"
    [id]="protocolId()"
    [attr.aria-label]="'testRunner.runnerJob.protocolAria' | translate"
    [hidden]="!expanded()"
  >
    @switch (protocolState()) {
      @case ('loading') {
        <p class="runner-job-card__hint" role="status" data-testid="runner-job-protocol-loading">
          {{ 'testRunner.runnerJob.protocolLoading' | translate }}
        </p>
      }
      @case ('error') {
        <p class="runner-job-card__protocol-error" role="alert" data-testid="runner-job-protocol-error">
          <mat-icon aria-hidden="true">error_outline</mat-icon>
          <span>{{ 'testRunner.runnerJob.protocolError' | translate }}</span>
          <button mat-button type="button" data-testid="runner-job-protocol-retry" (click)="loadProtocol()">
            {{ 'testRunner.runnerJob.protocolRetry' | translate }}
          </button>
        </p>
      }
      @case ('loaded') {
        @if (protocolText()) {
          <pre class="runner-job-card__log" tabindex="0">{{ protocolText() }}</pre>
        } @else {
          <p class="runner-job-card__hint" data-testid="runner-job-protocol-empty">
            {{ 'testRunner.runnerJob.protocolEmpty' | translate }}
          </p>
        }
      }
    }
  </div>

  <app-confirm-dialog
    icon="stop_circle"
    [visible]="cancelConfirming()"
    [busy]="cancelling()"
    [title]="'testCases.run.cancel.confirmTitle' | translate"
    [message]="'testCases.run.cancel.confirmMessage' | translate"
    [confirmLabel]="'testCases.run.cancel.confirmConfirm' | translate"
    [cancelLabel]="'testCases.run.cancel.confirmCancel' | translate"
    (confirmed)="confirmCancel()"
    (cancelled)="dismissCancel()"
  />
</section>
